import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Checkbox,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import axios from '../../../api/axios';

const SubscriptionDetailsDialog = ({
  open,
  onClose,
  onConfirm,
  subscriptionId,
  title = 'Subscription Details',
  confirmText = 'Confirm',
  message
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isEditingDoubleBox, setIsEditingDoubleBox] = useState(false);
  const [selectedDoubleBoxDays, setSelectedDoubleBoxDays] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const dayNames = useMemo(() => ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], []);

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (open && subscriptionId) {
        setIsLoading(true);
        try {
          const response = await axios.get(`subscription/get/?subscription_id=${subscriptionId}`);
          setSubscriptionDetails(response.data.subscription);

          // Convert day names to indices for editing
          if (response.data.subscription.double_box_days) {
            const dayIndices = response.data.subscription.double_box_days.map(dayName =>
              dayNames.findIndex(name => name === dayName)
            ).filter(index => index !== -1);
            setSelectedDoubleBoxDays(dayIndices);
          } else {
            setSelectedDoubleBoxDays([]);
          }
        } catch (error) {
          console.error('Error fetching subscription details:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchSubscriptionDetails();
    return () => {
      setSubscriptionDetails(null);
      setIsEditingDoubleBox(false);
      setSelectedDoubleBoxDays([]);
    };
  }, [open, subscriptionId, dayNames]);

  const handleEditDoubleBoxDays = () => {
    setIsEditingDoubleBox(true);
  };

  const handleCancelEdit = () => {
    setIsEditingDoubleBox(false);
    // Reset to original values
    if (subscriptionDetails?.double_box_days) {
      const dayIndices = subscriptionDetails.double_box_days.map(dayName =>
        dayNames.findIndex(name => name === dayName)
      ).filter(index => index !== -1);
      setSelectedDoubleBoxDays(dayIndices);
    } else {
      setSelectedDoubleBoxDays([]);
    }
  };

  const handleUpdateDoubleBoxDays = async () => {
    setIsUpdating(true);
    try {
      const response = await axios.post('subscription/update/', {
        subscription_id: subscriptionId,
        double_box_days: selectedDoubleBoxDays
      });

      // Update local state with new data
      setSubscriptionDetails(prev => ({
        ...prev,
        double_box_days: selectedDoubleBoxDays.map(index => dayNames[index])
      }));

      setIsEditingDoubleBox(false);
    } catch (error) {
      console.error('Error updating double box days:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDoubleBoxDayChange = (event) => {
    const value = event.target.value;
    setSelectedDoubleBoxDays(typeof value === 'string' ? value.split(',').map(Number) : value);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : subscriptionDetails ? (
          <Grid container spacing={2}>
            {/* User Details */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>User Information</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>ID:</strong> {subscriptionDetails.user.id}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Name:</strong> {subscriptionDetails.user.full_name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {subscriptionDetails.user.email}
              </Typography>
            </Grid>

            {/* Subscription Status */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom>Subscription Status</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Status:</strong> {subscriptionDetails.status}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Expired:</strong> {subscriptionDetails.expired ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Purchase Date:</strong> {subscriptionDetails.purchase_date}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Coupon Code:</strong> {subscriptionDetails.coupon_code || 'None'}
              </Typography>
            </Grid>

            {/* Package Details */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>Package Details</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Category:</strong> {subscriptionDetails.package.category.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Duration:</strong> {subscriptionDetails.package.duration} days
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Custom Package:</strong> {subscriptionDetails.package.is_custom ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Price:</strong> KWD {subscriptionDetails.package.price?.toFixed(2)}
              </Typography>
            </Grid>

            {/* Meal Details */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>Daily Meals</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Breakfast:</strong> {subscriptionDetails.package.daily_breakfast || 0}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Meals:</strong> {subscriptionDetails.package.daily_meal || 0}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Snacks:</strong> {subscriptionDetails.package.daily_snack || 0}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Sweets:</strong> {subscriptionDetails.package.daily_sweet || 0}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Protein Level:</strong> {subscriptionDetails.package.protein_level}
              </Typography>
            </Grid>

            {/* Subscription Dates & Double Box Days */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle1" gutterBottom>Dates & Special Days</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Start Date:</strong> {subscriptionDetails.start_date}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>End Date:</strong> {subscriptionDetails.end_date}
              </Typography>

              {(subscriptionDetails.double_box_days && subscriptionDetails.double_box_days.length > 0) || isEditingDoubleBox ? (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Double Box Days:</strong>
                    </Typography>
                    {!isEditingDoubleBox && (
                      <IconButton size="small" onClick={handleEditDoubleBoxDays} sx={{ ml: 0.5 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>

                  {isEditingDoubleBox ? (
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Days</InputLabel>
                      <Select
                        multiple
                        value={selectedDoubleBoxDays}
                        onChange={handleDoubleBoxDayChange}
                        input={<OutlinedInput label="Select Days" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip
                                key={value}
                                label={dayNames[value]}
                                size="small"
                                sx={{
                                  backgroundColor: '#4CAF50',
                                  color: '#FFF',
                                  fontSize: '0.65rem',
                                  height: '20px'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {dayNames.map((day, index) => (
                          <MenuItem key={index} value={index}>
                            <Checkbox
                              checked={selectedDoubleBoxDays.indexOf(index) > -1}
                              sx={{ mr: 1 }}
                            />
                            <ListItemText primary={day} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {subscriptionDetails.double_box_days?.map((day, index) => (
                        <Chip
                          key={index}
                          label={day}
                          size="small"
                          sx={{
                            backgroundColor: '#4CAF50',
                            color: '#FFF',
                            fontSize: '0.65rem',
                            height: '20px'
                          }}
                        />
                      )) || <Typography variant="body2" color="text.secondary">None selected</Typography>}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2">
                      <strong>Double Box Days:</strong>
                    </Typography>
                    <IconButton size="small" onClick={handleEditDoubleBoxDays} sx={{ ml: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="text.secondary">None selected</Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        ) : null}

        {message && (
          <Typography variant="body1" color="error" sx={{ mt: 2 }}>
            {message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isEditingDoubleBox ? (
          <>
            <Button onClick={handleCancelEdit}>Cancel</Button>
            <Button
              onClick={handleUpdateDoubleBoxDays}
              variant="contained"
              color="primary"
              disabled={isUpdating}
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDetailsDialog;